import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFilters } from '../../../context/FilterContext';
import { Box, MenuItem, Select, FormControl, InputLabel, Autocomplete, TextField, Button, CircularProgress, FormHelperText, Typography } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LearnerPracticesTable from './LearnerPracticesTable';
import { getAllCOE, getAllSemesters } from '../../../services/adminServices/CoeServices';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import { getLabType } from '../../../services/mentorServices/MentorServices';
import { getMentorPracticesDashboard, PracticeStatus, getPracticeNames, getLearnerList } from '../../../services/mentorServices/LearnerPracticeServices';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import './LearnerPractices.css';

const LearnerPractices = ({ isAdmin = false }) => {
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);
    const isViewer = user?.role === 'coe_report_viewer';
    const userCoeId = user?.coeId || user?.coe_id || user?.coeCenter?.id || user?.coe_center_id || user?.coe || '';

    const { learnerPracticesState, setLearnerPracticesState } = useFilters();

    const [statusFilter, setStatusFilter] = useState(() => {
        return isViewer ? PracticeStatus.COMPLETED : learnerPracticesState.statusFilter;
    });
    const [learnerFilter, setLearnerFilter] = useState(learnerPracticesState.learnerFilter);
    const [selectedLearnerId, setSelectedLearnerId] = useState(learnerPracticesState.selectedLearnerId);
    const [PracticeNameFilter, setPracticeNameFilter] = useState(learnerPracticesState.PracticeNameFilter);
    const [apiPracticeNames, setApiPracticeNames] = useState(learnerPracticesState.options.apiPracticeNames || []);
    const [coeLearnersList, setCoeLearnersList] = useState(learnerPracticesState.options.coeLearnersList || []);
    const [sortBy, setSortBy] = useState(learnerPracticesState.sortBy);
    const [loading, setLoading] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [currentPage, setCurrentPage] = useState(learnerPracticesState.currentPage);
    const [rowsPerPage, setRowsPerPage] = useState(learnerPracticesState.rowsPerPage);
    const [learnerInput, setLearnerInput] = useState(learnerPracticesState.learnerInput);
    const [PracticeNameInput, setPracticeNameInput] = useState(learnerPracticesState.PracticeNameInput);
    const [fromDate, setFromDate] = useState(learnerPracticesState.fromDate);
    const [toDate, setToDate] = useState(learnerPracticesState.toDate);

    const [selection, setSelection] = useState(() => {
        if (isViewer) {
            const baseSelection = learnerPracticesState.selection || (sessionStorage.getItem('MentorPracticeselection') ? JSON.parse(sessionStorage.getItem('MentorPracticeselection')) : null);
            return {
                lab: baseSelection?.lab || '',
                coe: userCoeId,
                labType: baseSelection?.labType || '',
                semester: baseSelection?.semester || ''
            };
        }
        if (learnerPracticesState.selection) return learnerPracticesState.selection;
        const saved = sessionStorage.getItem('MentorPracticeselection');
        if (saved) return JSON.parse(saved);
        return { lab: '', coe: userCoeId, labType: '', semester: '' };
    });
    const [coes, setCoe] = useState(learnerPracticesState.options.coes || []);
    const [labTypes, setLabTypes] = useState(learnerPracticesState.options.labTypes || []);
    const [semesters, setSemesters] = useState(learnerPracticesState.options.semesters || []);
    const [labNames, setLabNames] = useState(learnerPracticesState.options.labNames || []);
    const [error, setError] = useState({ lab: '', coe: '' });
    const [showTable, setShowTable] = useState(() => {
        if (learnerPracticesState.showTable !== null) return learnerPracticesState.showTable;
        const saved = sessionStorage.getItem('MentorPracticeshowTable');
        if (saved !== null) return saved === 'true';
        return false;
    });
    const [PracticesData, setPracticesData] = useState(learnerPracticesState.PracticesData);
    const [totalRecords, setTotalRecords] = useState(learnerPracticesState.totalRecords);
    const [stats, setStats] = useState(learnerPracticesState.stats);

    useEffect(() => {
        if (isViewer && userCoeId && selection.coe !== userCoeId) {
            setSelection(prev => ({ ...prev, coe: userCoeId }));
        }
    }, [isViewer, userCoeId, selection.coe]);

    useEffect(() => {
        if (isViewer && statusFilter !== PracticeStatus.COMPLETED) {
            setStatusFilter(PracticeStatus.COMPLETED);
        }
    }, [isViewer, statusFilter]);

    useEffect(() => {
        setLearnerPracticesState({
            statusFilter: isViewer ? PracticeStatus.COMPLETED : statusFilter, learnerFilter, selectedLearnerId, PracticeNameFilter, sortBy,
            currentPage, rowsPerPage, learnerInput, PracticeNameInput, fromDate, toDate,
            selection, showTable, PracticesData, totalRecords, stats,
            options: { apiPracticeNames, coeLearnersList, coes, labNames, labTypes, semesters },
            hasFetched: learnerPracticesState.hasFetched
        });
    }, [statusFilter, learnerFilter, selectedLearnerId, PracticeNameFilter, sortBy,
        currentPage, rowsPerPage, learnerInput, PracticeNameInput, fromDate, toDate,
        selection, showTable, PracticesData, totalRecords, stats, 
        apiPracticeNames, coeLearnersList, coes, labNames, labTypes, semesters,
        setLearnerPracticesState, learnerPracticesState.hasFetched, isViewer]);

    const fetchPracticeNamesList = async () => {
        try {
            const response = await getPracticeNames();
            const names = response?.data?.data?.practiceNames || response?.data?.practiceNames || response?.data?.data?.PracticeNames || response?.data?.PracticeNames || [];
            if (Array.isArray(names)) {
                setApiPracticeNames(names);
            }
        } catch (err) {
            console.error("Failed to fetch Practice names:", err);
        }
    };

    const fetchLearnersForLab = useCallback(async (coeId, labId) => {
        if (!coeId) {
            setCoeLearnersList([]);
            return;
        }
        try {
            const response = await getLearnerList(coeId, labId);
            const resData = response?.data?.data || response?.data;
            let list = [];
            
            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object' && Array.isArray(resData.learners)) {
                list = resData.learners;
            }

            setCoeLearnersList(list);
        } catch (err) {
            console.error("Failed to fetch learners for lab:", err);
            setCoeLearnersList([]);
        }
    }, []);

    const getCoe = async () => {
        try {
            const response = await getAllCOE();
            setCoe(response?.data?.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch COEs");
        }
    };

    const fetchLabTypes = async () => {
        try {
            const response = await getLabType();
            setLabTypes(response?.data?.data?.value || response?.data?.data || []);
        } catch (err) {
            console.error("Failed to fetch lab types:", err);
            setLabTypes([]);
        }
    };

    const fetchSemesters = useCallback(async (coeId) => {
        if (!coeId) {
            setSemesters([]);
            return;
        }
        try {
            const response = await getAllSemesters(coeId);
            const sortedSemesters = response?.data?.data?.sort((a, b) => a - b) || [];
            setSemesters(sortedSemesters);
        } catch (err) {
            console.error("Failed to fetch semesters:", err);
            setSemesters([]);
        }
    }, []);

    const fetchLabs = useCallback(async (coeId, labType = '', semester = '') => {
        if (!coeId) {
            setLabNames([]);
            return;
        }
        try {
            const response = await getAllCoeLabs(coeId, labType, semester);
            setLabNames(response?.data?.data || []);
        } catch (err) {
            setLabNames([]);
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch Labs");
        }
    }, []);

    useEffect(() => {
        if (!isViewer && coes.length === 0) getCoe();
        if (labTypes.length === 0) fetchLabTypes();
        if (apiPracticeNames.length === 0) fetchPracticeNamesList();
        if (selection.coe) {
            if (semesters.length === 0) fetchSemesters(selection.coe);
            if (labNames.length === 0) fetchLabs(selection.coe, selection.labType, selection.semester);
        }
        if (selection.coe && coeLearnersList.length === 0) {
            fetchLearnersForLab(selection.coe, selection.lab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selection.coe && semesters.length === 0) {
            fetchSemesters(selection.coe);
        }
    }, [selection.coe, semesters.length, fetchSemesters]);

    useEffect(() => {
        sessionStorage.setItem('MentorPracticeselection', JSON.stringify(selection));
    }, [selection]);

    useEffect(() => {
        sessionStorage.setItem('MentorPracticeshowTable', showTable);
    }, [showTable]);

    const handleSelectionChange = (event) => {
        const { name, value } = event.target;
        setSelection(prev => {
            if (name === 'coe') {
                fetchSemesters(value);
                fetchLabs(value, prev.labType, prev.semester);
                fetchLearnersForLab(value, '');
                return { ...prev, coe: value, lab: '', semester: '' };
            }
            if (name === 'labType') {
                fetchLabs(prev.coe, value, prev.semester);
                return { ...prev, labType: value, lab: '' };
            }
            if (name === 'semester') {
                fetchLabs(prev.coe, prev.labType, value);
                return { ...prev, semester: value, lab: '' };
            }
            const updated = { ...prev, [name]: value };
            if (updated.coe) {
                fetchLearnersForLab(updated.coe, updated.lab);
            }
            return updated;
        });
        setError(prev => ({ ...prev, [name]: '' }));
        setShowTable(false);
        setLearnerFilter('all');
        setSelectedLearnerId(null);
        setPracticeNameFilter('all');
        setLearnerInput('All Learners');
        setPracticeNameInput('All Practices');
        setFromDate('');
        setToDate('');
        setCurrentPage(1);
    };

    const fetchData = useCallback(async (customPage = 1, customLimit = 10, filterOverrides = {}) => {
        const currentLab = filterOverrides.lab !== undefined ? filterOverrides.lab : selection.lab;
        const { coe } = selection;

        if (!coe) {
            setError({ coe: 'Please select a COE' });
            return;
        }

        // Ensure learners list is loaded
        if (coeLearnersList.length === 0 && coe) {
            fetchLearnersForLab(coe, currentLab);
        }

        setLoading(true);
        try {
            const effectiveStatusFilter = isViewer ? PracticeStatus.COMPLETED : statusFilter;
            const currentStatus = isViewer ? PracticeStatus.COMPLETED : (filterOverrides.status !== undefined ? filterOverrides.status : effectiveStatusFilter);
            const currentLearner = filterOverrides.learner !== undefined ? filterOverrides.learner : learnerFilter;
            const currentLearnerId = filterOverrides.learnerId !== undefined ? filterOverrides.learnerId : selectedLearnerId;
            const currentPracticeName = filterOverrides.practiceName !== undefined ? filterOverrides.practiceName : (filterOverrides.PracticeName !== undefined ? filterOverrides.PracticeName : PracticeNameFilter);
            const currentSort = filterOverrides.sort !== undefined ? filterOverrides.sort : sortBy;
            const currentFromDate = filterOverrides.fromDate !== undefined ? filterOverrides.fromDate : fromDate;
            const currentToDate = filterOverrides.toDate !== undefined ? filterOverrides.toDate : toDate;

            const apiParams = {
                coe_id: coe,
                lab_id: currentLab || undefined,
                lab_type: selection.labType || undefined,
                semester: selection.semester || undefined,
                learnerId: currentLearnerId || undefined,
                learner_name: (!currentLearnerId && currentLearner !== 'all') ? currentLearner : undefined,
                practiceName: currentPracticeName !== 'all' ? currentPracticeName : undefined,
                status: currentStatus !== 'all' ? currentStatus : undefined,
                sort: currentSort,
                page: customPage,
                limit: customLimit,
                from_date: currentFromDate ? currentFromDate + "T00:00:00.000Z" : undefined,
                to_date: currentToDate ? currentToDate + "T23:59:59.000Z" : undefined
            };

            const response = await getMentorPracticesDashboard(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];

            if (Array.isArray(resData)) {
                list = resData;
                setTotalRecords(response?.data?.pagination?.totalItems || response?.data?.totalRecords || list.length);
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.practices) ? resData.practices : (Array.isArray(resData.Practices) ? resData.Practices : (Array.isArray(resData.data) ? resData.data : (Array.isArray(resData.list) ? resData.list : [])));
                setTotalRecords(resData.pagination?.totalItems || resData.totalRecords || resData.totalCount || list.length);
            }

            setPracticesData(list);
            setShowTable(true);

            if (resData?.summary) {
                setStats({
                    totalLearners: resData.summary.totalLearners || 0,
                    totalPractices: resData.summary.totalPractices || 0,
                    completedPractices: resData.summary.totalCompletedPractices || 0,
                    pendingPractices: resData.summary.totalInProgress || resData.summary.totalVerificationPending || 0,
                    evaluationPending: resData.summary.totalEvaluationPending ?? resData.summary.evaluationPending,
                    evaluationFailed: resData.summary.totalEvaluationFailed ?? resData.summary.evaluationFailed,
                });
            } else if (resData?.stats) {
                setStats({
                    totalLearners: resData.stats.totalLearners || 0,
                    totalPractices: resData.stats.totalPractices || 0,
                    completedPractices: resData.stats.totalCompletedPractices || resData.stats.completedPractices || 0,
                    pendingPractices: resData.stats.totalVerificationPending || resData.stats.pendingPractices || 0,
                    evaluationPending: resData.stats.totalEvaluationPending ?? resData.stats.evaluationPending,
                    evaluationFailed: resData.stats.totalEvaluationFailed ?? resData.stats.evaluationFailed,
                });
            } else if (resData && typeof resData === 'object' && !Array.isArray(resData) && (resData.totalLearners !== undefined || resData.totalPractices !== undefined)) {
                setStats({
                    totalLearners: resData.totalLearners || 0,
                    totalPractices: resData.totalPractices || 0,
                    completedPractices: resData.totalCompletedPractices || resData.completedPractices || 0,
                    pendingPractices: resData.totalInProgress || resData.totalVerificationPending || resData.pendingPractices || 0,
                    evaluationPending: resData.totalEvaluationPending ?? resData.evaluationPending,
                    evaluationFailed: resData.totalEvaluationFailed ?? resData.evaluationFailed,
                });
            } else {
                const totalRev = resData?.pagination?.totalItems || resData?.totalRecords || list.length;
                const completedRev = list.filter(item => (item?.status || item?.aiGradingStatus?.coding || '').toLowerCase() === 'completed').length;
                const pendingRev = list.filter(item => (item?.status || item?.aiGradingStatus?.coding || '').toLowerCase() === 'pending').length;
                const totalLearn = new Set(list.map(item => item?.learnerName || item?.learner_name || item?.Learner?.name || item?.learner?.name || item?.name)).size;
                setStats({
                    totalLearners: totalLearn,
                    totalPractices: totalRev,
                    completedPractices: completedRev,
                    pendingPractices: pendingRev,
                    evaluationPending: undefined,
                    evaluationFailed: undefined
                });
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || err?.error || "Failed to fetch Practices");
            setPracticesData([]);
            setTotalRecords(0);
            setShowTable(false);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection, statusFilter, learnerFilter, PracticeNameFilter, sortBy, fromDate, toDate, isViewer]);

    useEffect(() => {
        // Initial fetch if table was previously shown
        const user = JSON.parse(localStorage.getItem('user')) || {};
        const isViewer = user.role === 'coe_report_viewer';

        if ((showTable || isViewer) && selection.coe && !learnerPracticesState.hasFetched) {
            setLearnerPracticesState(prev => ({ ...prev, hasFetched: true }));
            fetchData(currentPage, rowsPerPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadAllCSV = async () => {
        const { lab, coe } = selection;
        if (!coe) {
            toast.info("Please select a COE first.");
            return;
        }

        const countToFetch = totalRecords > 0 ? totalRecords : (filteredData?.length || 10);
        if (countToFetch === 0) {
            toast.info("No Learner Review data available to download.");
            return;
        }

        setDownloadingCSV(true);
        try {
            const effectiveStatus = isViewer ? PracticeStatus.COMPLETED : statusFilter;
            const apiParams = {
                page: 1,
                limit: countToFetch,
                status: effectiveStatus !== 'all' ? effectiveStatus : undefined,
                learnerId: selectedLearnerId || undefined,
                coe_id: coe,
                PracticeName: PracticeNameFilter !== 'all' ? PracticeNameFilter : undefined,
                sort: sortBy,
                lab_id: lab || undefined,
                lab_type: selection.labType || undefined,
                semester: selection.semester || undefined,
                from_date: fromDate ? fromDate + "T00:00:00.000Z" : undefined,
                to_date: toDate ? toDate + "T23:59:59.000Z" : undefined,
            };

            const response = await getMentorPracticesDashboard(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];

            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.practices) ? resData.practices : (Array.isArray(resData.Practices) ? resData.Practices : (Array.isArray(resData.data) ? resData.data : (Array.isArray(resData.list) ? resData.list : [])));
            }

            if (!list || list.length === 0) {
                toast.info("No Learner Review data available to download.");
                return;
            }

            const formattedData = list.map((item, index) => {
                const learnerName = item.learnerName || item.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || "-";
                const emailId = item.emailId || item.email_id || item?.Learner?.emailId || item?.Learner?.email || item?.Learner?.email_id || item?.learner?.email || item?.email || item.institutionEmail || "-";
                const labName = item.labName || item.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.learner?.lab_name || item?.learner?.labName || "-";
                const learnerId = item.learnerId || item.learner_id || "-";
                const rollNo = item.rollNo || item.rollNumber || item.roll_number || item?.Learner?.rollNumber || item?.Learner?.roll_number || item?.Learner?.rollNo || item?.Learner?.rollNum || item?.learner?.roll_num || item?.roll_num || item?.rollNum || item.learnerCode || "-";
                const practiceTitle = item.practiceTitle || "-";
                const practiceName = item.practiceName || item.PracticeName || item.PracticeTitle || item.Practice_name || "-";
                const practiceLevel = item.practiceLevel || "-";
                
                const subDateRaw = item.submissionDate || item.submission_date || item.created_at || item.submittedDate || item.submitted_date;
                const subDate = subDateRaw ? new Date(subDateRaw).toLocaleString('en-US') : "-";

                const qReady = item?.gradingStatus?.quiz === 'ready' ? 'ready' : 'pending';
                const tReady = item?.gradingStatus?.technical === 'ready' ? 'ready' : 'pending';
                const cReady = item?.gradingStatus?.coding === 'ready' ? 'ready' : 'pending';
                const gradingStatusStr = `Q: ${qReady} | T: ${tReady} | C: ${cReady}`;

                const status = (item.status || "PENDING").replace(/_/g, ' ');

                return {
                    "S.No": index + 1,
                    "Roll Number": rollNo,
                    "Learner Name": learnerName,
                    "Email": emailId,
                    "Lab Name": labName,
                    "Practice Name": practiceName,
                    "Level": practiceLevel,
                    "Submission Date": subDate,
                    "Status": status
                };
            });

            const csvContent = Papa.unparse(formattedData);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const cleanName = (PracticeNameFilter && PracticeNameFilter !== 'all')
                ? PracticeNameFilter.replace(/[^a-zA-Z0-9]/g, '_')
                : 'All_Practices';
            link.setAttribute("download", `Learner_Practice_Scores_${cleanName}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success("CSV file downloaded successfully!");
        } catch (err) {
            console.error("Failed to download full scores CSV:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to download scores CSV.");
        } finally {
            setDownloadingCSV(false);
        }
    };

    const isActionDisabled = !selection.coe;

    const learnerOptions = useMemo(() => {
        const map = new Map();

        coeLearnersList.forEach(item => {
            const l = item?.learner || item;
            const name = l?.name || l?.fullName || l?.learner_name;
            const id = l?.learner_id || l?.learnerId || l?.id || item?.learner_id || item?.id;
            const rollNum = l?.roll_num || l?.rollNum || l?.roll_no || l?.rollNo || item?.roll_num || '';

            if (name) {
                const label = rollNum ? `${name} - ${rollNum}` : name;
                const key = id ? String(id) : `${name.toLowerCase()}_${rollNum.toLowerCase()}`;
                map.set(key, {
                    label: label,
                    value: key,
                    learnerId: id || null,
                    rawName: name,
                    rollNum: rollNum
                });
            }
        });

        PracticesData.forEach(item => {
            const l = item?.learner || item?.Learner;
            const name = item?.learnerName || item?.learner_name || l?.name || item?.name;
            const id = item?.learnerId || item?.learner_id || l?.id || l?.learner_id;
            const rollNum = item?.roll_num || item?.rollNum || l?.roll_num || '';

            if (name) {
                const key = id ? String(id) : `${name.toLowerCase()}_${rollNum.toLowerCase()}`;
                if (!map.has(key)) {
                    const label = rollNum ? `${name} - ${rollNum}` : name;
                    map.set(key, {
                        label: label,
                        value: key,
                        learnerId: id || null,
                        rawName: name,
                        rollNum: rollNum
                    });
                }
            }
        });

        const sortedMapOptions = Array.from(map.values()).sort((a, b) => {
            return (a.label || '').localeCompare((b.label || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        const options = [{ label: 'All Learners', value: 'all', learnerId: null, rawName: 'all', rollNum: '' }];
        sortedMapOptions.forEach(opt => {
            options.push(opt);
        });
        return options;
    }, [coeLearnersList, PracticesData]);

    const PracticeNameOptions = useMemo(() => {
        const names = new Set(apiPracticeNames);
        PracticesData.forEach(item => {
            const rName = item?.practiceName || item?.PracticeName || item?.PracticeTitle || item?.Practice_name || item?.Practice?.name;
            if (rName) names.add(rName);
        });
        const sortedNames = Array.from(names).sort((a, b) => {
            return (a || '').localeCompare((b || ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        const options = [{ label: 'All Practices', value: 'all' }];
        sortedNames.forEach(name => {
            options.push({ label: name, value: name });
        });
        return options;
    }, [apiPracticeNames, PracticesData]);

    const filteredData = useMemo(() => {
        const effectiveStatus = isViewer ? PracticeStatus.COMPLETED : statusFilter;
        return PracticesData.filter(item => {
            const status = (item?.status || '').toUpperCase();
            if (effectiveStatus !== 'all' && status !== effectiveStatus.toUpperCase()) {
                return false;
            }
            if (learnerFilter !== 'all') {
                const itemLearnerId = item?.learnerId || item?.learner_id || item?.Learner?.id || item?.learner?.id;
                if (selectedLearnerId && itemLearnerId) {
                    if (String(itemLearnerId) !== String(selectedLearnerId)) {
                        return false;
                    }
                } else {
                    const name = (item?.learnerName || item?.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || '').toLowerCase();
                    const selectedOpt = learnerOptions.find(o => o.value === learnerFilter);
                    const targetName = (selectedOpt?.rawName || learnerFilter).toLowerCase();
                    if (!name.includes(targetName)) {
                        return false;
                    }
                }
            }
            if (PracticeNameFilter !== 'all') {
                const rName = (item?.practiceName || item?.PracticeName || item?.PracticeTitle || item?.Practice_name || item?.Practice?.name || '').toLowerCase();
                if (!rName.includes(PracticeNameFilter.toLowerCase())) {
                    return false;
                }
            }
            return true;
        });
    }, [PracticesData, statusFilter, learnerFilter, selectedLearnerId, PracticeNameFilter, sortBy, learnerOptions, isViewer]);

    const formatCount = (count) => count < 10 ? `0${count}` : count;
    const formatCountOrDash = (count) => (count === undefined || count === null) ? '-' : formatCount(count);

    const inProgressCount = useMemo(() => {
        const parseCount = (val) => {
            if (val === null || val === undefined || val === '-') return 0;
            const num = Number(val);
            return isNaN(num) ? 0 : num;
        };
        const pending = parseCount(stats.pendingPractices);
        const evalPending = parseCount(stats.evaluationPending);
        const evalFailed = parseCount(stats.evaluationFailed);
        const total = parseCount(stats.totalPractices);
        const completed = parseCount(stats.completedPractices);
        if (total > 0 && completed + pending === total) {
            return pending;
        }
        return pending + evalPending + evalFailed;
    }, [stats]);

    const selectStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0b0b0',
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    };

    return (
        <Box className="learner-review-container">
            <Box className="learner-review-filters-container" sx={{ mb: 3, justifyContent: 'flex-start !important', gap: 2, flexWrap: 'wrap' }}>
                {!isViewer && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: 180 }, maxWidth: { md: 280 } }} error={!!error.coe}>
                        <InputLabel id="coe-select-label">COE*</InputLabel>
                        <Select
                            labelId="coe-select-label"
                            id="coe-select"
                            name="coe"
                            value={selection.coe}
                            onChange={handleSelectionChange}
                            label="COE"
                            sx={selectStyle}
                        >
                            {coes.map((coe) => (
                                <MenuItem key={coe.id} value={coe.id}>
                                    {coe.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
                    </FormControl>
                )}

                {!isViewer && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: { xs: '100%', sm: 160 }, maxWidth: { md: 240 } }}>
                        <InputLabel id="lab-type-select-label" shrink>Lab Type</InputLabel>
                        <Select
                            labelId="lab-type-select-label"
                            id="lab-type-select"
                            name="labType"
                            value={selection.labType || ''}
                            onChange={handleSelectionChange}
                            label="Lab Type"
                            notched
                            displayEmpty
                            renderValue={(selected) => selected || "All Lab Types"}
                            sx={selectStyle}
                        >
                            <MenuItem value="">All Lab Types</MenuItem>
                            {labTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: { xs: '100%', sm: 160 }, maxWidth: { md: 240 } }}>
                    <InputLabel id="semester-select-label" shrink>Semester</InputLabel>
                    <Select
                        labelId="semester-select-label"
                        id="semester-select"
                        name="semester"
                        value={selection.semester || ''}
                        onChange={handleSelectionChange}
                        label="Semester"
                        notched
                        displayEmpty
                        renderValue={(selected) => (selected !== '' && selected !== null && selected !== undefined) ? selected : "All Semesters"}
                        sx={selectStyle}
                        disabled={!selection.coe}
                    >
                        <MenuItem value="">All Semesters</MenuItem>
                        {semesters.filter(sem => sem != null).map((sem) => (
                            <MenuItem key={sem} value={sem}>
                                {sem}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {(!isAdmin || isViewer) && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: 180 }, maxWidth: { md: 280 } }} error={!!error.lab}>
                        <InputLabel id="lab-select-label" shrink>Lab Name</InputLabel>
                        <Select
                            labelId="lab-select-label"
                            id="lab-select"
                            name="lab"
                            value={selection.lab || ''}
                            onChange={handleSelectionChange}
                            label="Lab Name"
                            notched
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return "All Labs";
                                const found = labNames.find((lab) => lab.id === selected);
                                return found ? found.lab_name : selected;
                            }}
                            sx={selectStyle}
                        >
                            <MenuItem value="">All Labs</MenuItem>
                            {labNames.map((lab) => (
                                <MenuItem key={lab.id} value={lab.id}>
                                    {lab.lab_name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.lab && <FormHelperText>{error.lab}</FormHelperText>}
                    </FormControl>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => fetchData(1, 10)}
                    sx={{ borderRadius: '8px', textTransform: 'none', height: '40px', px: 4, fontWeight: 600, width: { xs: '100%', sm: 'auto' }, alignSelf: { sm: 'center' } }}
                    disabled={loading || isActionDisabled}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'VIEW'}
                </Button>
            </Box>

            {!showTable || (loading && PracticesData.length === 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '350px', width: '100%', mt: 4 }}>
                    {loading ? (
                        <CircularProgress size={60} />
                    ) : (
                        <>
                            <SchoolOutlinedIcon sx={{ fontSize: 100, color: '#e0e0e0', mb: 2 }} />
                            <Typography sx={{ color: '#5f6368', fontSize: '18px', fontWeight: 400, textAlign: 'center' }}>
                                Learner details will appear here after selecting COE.
                            </Typography>
                        </>
                    )}
                </Box>
            ) : (
                <>
                    <Box className={`learner-practice-stats-grid ${isViewer ? 'viewer-stats-grid' : ''}`}>
                        <Box className="learner-review-stat-card">
                            <span className="learner-review-stat-title">Total Learners</span>
                            <span className="learner-review-stat-value">{formatCount(stats.totalLearners)}</span>
                        </Box>
                        <Box className="learner-review-stat-card">
                            <span className="learner-review-stat-title">Total Practices</span>
                            <span className="learner-review-stat-value">{formatCount(stats.totalPractices)}</span>
                        </Box>
                        <Box className="learner-review-stat-card completed">
                            <span className="learner-review-stat-title">Total Completed </span>
                            <span className="learner-review-stat-value">{formatCount(stats.completedPractices)}</span>
                        </Box>
                        <Box className="learner-review-stat-card pending in-progress">
                            <span className="learner-review-stat-title">Total In Progress </span>
                            <span className="learner-review-stat-value">{formatCount(inProgressCount)}</span>
                        </Box>
                        {!isViewer && (
                            <>
                                <Box className="learner-review-stat-card evaluation-pending">
                                    <span className="learner-review-stat-title">Evaluation Pending </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.evaluationPending)}</span>
                                </Box>
                                <Box className="learner-review-stat-card evaluation-failed">
                                    <span className="learner-review-stat-title">Evaluation Failed </span>
                                    <span className="learner-review-stat-value">{formatCountOrDash(stats.evaluationFailed)}</span>
                                </Box>
                            </>
                        )}
                    </Box>

                    <Box className="learner-review-filters-container" sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Learners</span>
                                <Autocomplete
                                    size="small"
                                    options={learnerOptions}
                                    getOptionLabel={(option) => option?.label || ""}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                    value={learnerInput === '' ? null : (learnerOptions.find(opt => opt.value === learnerFilter) || learnerOptions[0] || null)}
                                    inputValue={learnerInput}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setLearnerInput(newInputValue);
                                        } else if (reason === 'clear') {
                                            setLearnerInput('');
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setCurrentPage(1);
                                        } else if (reason === 'reset') {
                                            if (newInputValue && newInputValue !== 'All Learners') {
                                                setLearnerInput(newInputValue);
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!learnerInput || !learnerInput.trim() || learnerFilter === 'all') {
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setLearnerInput('All Learners');
                                        } else {
                                            const current = learnerOptions.find(opt => opt.value === learnerFilter);
                                            setLearnerInput(current?.label || 'All Learners');
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!newValue) {
                                            setLearnerFilter('all');
                                            setSelectedLearnerId(null);
                                            setLearnerInput('');
                                            setCurrentPage(1);
                                            return;
                                        }
                                        const val = newValue.value;
                                        const lId = newValue.learnerId || null;
                                        setLearnerFilter(val);
                                        setSelectedLearnerId(lId);
                                        setLearnerInput(val === 'all' ? 'All Learners' : (newValue.label || ''));
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { learner: val, learnerId: lId });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search Learner" sx={selectStyle} />
                                    )}
                                    disableClearable={false}
                                />
                            </FormControl>

                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Practice Name</span>
                                <Autocomplete
                                    size="small"
                                    options={PracticeNameOptions}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : (option?.label || "")}
                                    isOptionEqualToValue={(option, value) => {
                                        const optVal = typeof option === 'string' ? option : option?.value;
                                        const valVal = typeof value === 'string' ? value : value?.value;
                                        return optVal === valVal;
                                    }}
                                    value={PracticeNameInput === '' ? null : (PracticeNameOptions.find(opt => opt.value === PracticeNameFilter) || (PracticeNameFilter !== 'all' ? (typeof PracticeNameFilter === 'string' ? { label: PracticeNameFilter, value: PracticeNameFilter } : null) : PracticeNameOptions[0]) || null)}
                                    inputValue={PracticeNameInput}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setPracticeNameInput(newInputValue);
                                        } else if (reason === 'clear') {
                                            setPracticeNameInput('');
                                            setPracticeNameFilter('all');
                                            setCurrentPage(1);
                                        } else if (reason === 'reset') {
                                            if (newInputValue && newInputValue !== 'All Practices') {
                                                setPracticeNameInput(newInputValue);
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!PracticeNameInput || !PracticeNameInput.trim() || PracticeNameFilter === 'all') {
                                            setPracticeNameFilter('all');
                                            setPracticeNameInput('All Practices');
                                        } else {
                                            const current = PracticeNameOptions.find(opt => opt.value === PracticeNameFilter);
                                            setPracticeNameInput(typeof current === 'string' ? current : (current?.label || 'All Practices'));
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (!newValue) {
                                            setPracticeNameFilter('all');
                                            setPracticeNameInput('');
                                            setCurrentPage(1);
                                            return;
                                        }
                                        const val = typeof newValue === 'string' ? newValue : (newValue.value || 'all');
                                        const label = typeof newValue === 'string' ? newValue : (newValue.label || val);
                                        setPracticeNameFilter(val);
                                        setPracticeNameInput(val === 'all' ? 'All Practices' : label);
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { practiceName: val });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search Practice Name" sx={selectStyle} />
                                    )}
                                />
                            </FormControl>

                            {isAdmin && !isViewer && (
                                <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Lab Name</span>
                                    <Select
                                        value={selection.lab || 'all'}
                                        onChange={(e) => {
                                            const val = e.target.value === 'all' ? '' : e.target.value;
                                            setSelection(prev => {
                                                const updated = { ...prev, lab: val };
                                                if (updated.coe) {
                                                    fetchLearnersForLab(updated.coe, updated.lab);
                                                }
                                                return updated;
                                            });
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { lab: val });
                                        }}
                                        sx={selectStyle}
                                    >
                                        <MenuItem value="all">All Labs</MenuItem>
                                        {labNames.map((lab) => (
                                            <MenuItem key={lab.id} value={lab.id}>
                                                {lab.lab_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {!isViewer && (
                                <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '150px' } }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>Status</span>
                                    <Select
                                        value={statusFilter}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setStatusFilter(val);
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { status: val });
                                        }}
                                        sx={selectStyle}
                                    >
                                        <MenuItem value="all">All Statuses</MenuItem>
                                        {/* <MenuItem value={PracticeStatus.NOT_STARTED}>Not Started</MenuItem> */}
                                        <MenuItem value={PracticeStatus.IN_PROGRESS}>In Progress</MenuItem>
                                        <MenuItem value={PracticeStatus.EVALUATION_PENDING}>Evaluation Pending</MenuItem>
                                        <MenuItem value={PracticeStatus.EVALUATION_FAILED}>Evaluation Failed</MenuItem>
                                        <MenuItem value={PracticeStatus.COMPLETED}>Completed</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            
                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '140px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>From Date</span>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={fromDate}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFromDate(val);
                                        if (val && toDate && new Date(val) > new Date(toDate)) {
                                            if (new Date(val).getFullYear() > 999) {
                                                toast.error("From Date cannot be later than To Date");
                                            }
                                            return;
                                        }
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { fromDate: val });
                                    }}
                                    sx={selectStyle}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>

                            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: '140px' } }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase' }}>To Date</span>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={toDate}
                                    inputProps={{ min: fromDate }}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setToDate(val);
                                        if (val && fromDate && new Date(val) < new Date(fromDate)) {
                                            if (new Date(val).getFullYear() > 999) {
                                                toast.error("To Date cannot be earlier than From Date");
                                            }
                                            return;
                                        }
                                        setCurrentPage(1);
                                        fetchData(1, rowsPerPage, { toDate: val });
                                    }}
                                    sx={selectStyle}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>
                    </Box>

                    <Box className="dashboard-box-container" sx={{ width: '100%', mt: 0 }}>
                        <LearnerPracticesTable
                            data={filteredData}
                            loading={loading}
                            totalCount={totalRecords}
                            currentPage={currentPage}
                            selectedPracticeName={PracticeNameFilter}
                            onPageChange={(page, limit) => {
                                setCurrentPage(page);
                                fetchData(page, limit);
                            }}
                            onRowsPerPageChange={(page, limit) => {
                                setCurrentPage(1);
                                setRowsPerPage(limit);
                                fetchData(1, limit);
                            }}
                            onRefresh={() => fetchData(currentPage, rowsPerPage)}
                            onDownloadCSV={handleDownloadAllCSV}
                            downloadingCSV={downloadingCSV}
                            isAdmin={isAdmin}
                            sortFilterNode={
                                <FormControl size="small" sx={{ minWidth: '160px' }}>
                                    <Select
                                        value={sortBy}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSortBy(val);
                                            setCurrentPage(1);
                                            fetchData(1, rowsPerPage, { sort: val });
                                        }}
                                        sx={selectStyle}
                                        displayEmpty
                                    >
                                        <MenuItem value="latest">Latest Submissions</MenuItem>
                                        <MenuItem value="oldest">Oldest Submissions</MenuItem>
                                    </Select>
                                </FormControl>
                            }
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default LearnerPractices;

